import React from 'react';
import { Col, Progress, Row, Skeleton, Space, Tooltip, Typography } from 'antd';
import { DollarOutlined, HomeOutlined, InfoCircleOutlined, UserOutlined } from '@ant-design/icons';
import { PerformanceSummary } from '../../../types/dashboard';
import { formatNumber, formatPercent, formatRupiah, getProgressColor } from '../utils';

const { Title, Text } = Typography;

interface SalesDetailSummaryCardsProps {
  summary: PerformanceSummary | null;
  loading: boolean;
  glassCardStyle: React.CSSProperties;
}

const SalesDetailSummaryCards: React.FC<SalesDetailSummaryCardsProps> = ({
  summary,
  loading,
  glassCardStyle
}) => {
  return (
    <Row gutter={[16, 16]} style={{ marginBottom: '20px' }}>
      {/* KPI 1: Omzet */}
      <Col xs={24} sm={8}>
        <div className="glass-card-interactive" style={{ ...glassCardStyle, padding: '16px 20px', borderRadius: '12px' }}>
          {loading || !summary ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Realisasi Omzet
                    </Text>
                    <Tooltip title="Jumlah nominal nilai transaksi yang telah sah (approved oleh Pusat) dikontribusikan oleh sales agent ini pada periode filter.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatRupiah(summary.omzet.realization)}
                  </Title>
                </div>
                <div style={{ padding: '8px', background: 'rgba(123, 170, 24, 0.15)', borderRadius: '8px' }}>
                  <DollarOutlined style={{ fontSize: '20px', color: '#7BAA18' }} />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                  <Text type="secondary">Target: {formatRupiah(summary.omzet.target)}</Text>
                  <Text strong style={{ color: getProgressColor(summary.omzet.percentage) }}>
                    {formatPercent(summary.omzet.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.omzet.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.omzet.percentage)}
                  strokeWidth={5}
                />
              </div>
            </>
          )}
        </div>
      </Col>

      {/* KPI 2: Unit Terjual */}
      <Col xs={24} sm={8}>
        <div className="glass-card-interactive" style={{ ...glassCardStyle, padding: '16px 20px', borderRadius: '12px' }}>
          {loading || !summary ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Unit Terjual
                    </Text>
                    <Tooltip title="Jumlah unit properti terjual yang disetujui (approved) diajukan oleh sales ini.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatNumber(summary.unit.realization)} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Unit</span>
                  </Title>
                </div>
                <div style={{ padding: '8px', background: 'rgba(24, 144, 255, 0.15)', borderRadius: '8px' }}>
                  <HomeOutlined style={{ fontSize: '20px', color: '#1890ff' }} />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                  <Text type="secondary">Target: {formatNumber(summary.unit.target)} Unit</Text>
                  <Text strong style={{ color: getProgressColor(summary.unit.percentage) }}>
                    {formatPercent(summary.unit.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.unit.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.unit.percentage)}
                  strokeWidth={5}
                />
              </div>
            </>
          )}
        </div>
      </Col>

      {/* KPI 3: Leads Target */}
      <Col xs={24} sm={8}>
        <div className="glass-card-interactive" style={{ ...glassCardStyle, padding: '16px 20px', borderRadius: '12px' }}>
          {loading || !summary ? (
            <Skeleton active paragraph={{ rows: 2 }} title={false} />
          ) : (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <Space size={6}>
                    <Text type="secondary" strong style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                      Jumlah Leads
                    </Text>
                    <Tooltip title="Jumlah total calon pembeli (leads) yang ditugaskan kepada sales agent ini.">
                      <InfoCircleOutlined style={{ color: '#8c8c8c', fontSize: '12px', cursor: 'pointer' }} />
                    </Tooltip>
                  </Space>
                  <Title level={3} style={{ margin: '4px 0 0 0', color: '#0f172a', fontWeight: 800 }}>
                    {formatNumber(summary.leads.realization)} <span style={{ fontSize: '14px', fontWeight: 'normal', color: '#64748b' }}>Leads</span>
                  </Title>
                </div>
                <div style={{ padding: '8px', background: 'rgba(123, 170, 24, 0.15)', borderRadius: '8px' }}>
                  <UserOutlined style={{ fontSize: '20px', color: '#7BAA18' }} />
                </div>
              </div>
              <div style={{ marginTop: '12px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', fontSize: '12px' }}>
                  <Text type="secondary">Target: {formatNumber(summary.leads.target)} Leads</Text>
                  <Text strong style={{ color: getProgressColor(summary.leads.percentage) }}>
                    {formatPercent(summary.leads.percentage)}
                  </Text>
                </div>
                <Progress
                  percent={summary.leads.percentage}
                  showInfo={false}
                  strokeColor={getProgressColor(summary.leads.percentage)}
                  strokeWidth={5}
                />
              </div>
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export default SalesDetailSummaryCards;
